/**
 * 时间线配置编辑器组件
 * 提供剧本时间线（幕/任务/事件）的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  message,
  Divider,
  Alert
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FlagOutlined
} from '@ant-design/icons'
import type { TimelineConfigData, TimelineEvent } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text, Title } = Typography

// 事件类型选项
const EVENT_TYPE_OPTIONS = [
  { value: 'video', label: '视频播放' },
  { value: 'broadcast', label: '系统广播' },
  { value: 'act_transition', label: '幕转换' },
  { value: 'check', label: '条件检查' },
  { value: 'trigger_event', label: '触发事件' },
  { value: 'ending_roll', label: '结局结算' },
  { value: 'game_end', label: '游戏结束' },
  { value: 'pause', label: '暂停节点' },
]

// 幕类型选项
const ACT_PHASE_OPTIONS = [
  { value: 'preparation', label: '准备阶段' },
  { value: 'investigation', label: '调查阶段' },
  { value: 'combat', label: '战斗阶段' },
  { value: 'ending', label: '结局阶段' },
]

// 时间线编辑器属性
interface TimelineEditorFormProps {
  initialData?: TimelineConfigData
  onSave: (data: TimelineConfigData) => Promise<void>
  loading?: boolean
}

// 主组件
const TimelineEditorForm: React.FC<TimelineEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        is_cyclic: initialData.is_cyclic,
      })
    } else {
      form.resetFields()
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const timelineData: TimelineConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        events: initialData?.events || [],
        is_cyclic: values.is_cyclic,
      }

      await onSave(timelineData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <span>{initialData?.name || '新建时间线'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="时间线键" rules={[{ required: true }]}>
                  <Input placeholder="例如: main_timeline" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="时间线名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 主线时间线" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="is_cyclic" label="是否循环" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="时间线的用途和说明..." />
            </Form.Item>
          </TabPane>

          <TabPane tab="事件列表" key="events">
            <Alert
              message="事件配置说明"
              description="时间线中的事件列表，包括定时事件、触发事件等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">
                时间线事件配置较为复杂，建议直接编辑 JSON 格式。
                事件数据存储在 events 字段中。
              </Text>
            </Card>
            <Form.Item name="events_json" label="事件配置 (JSON)">
              <TextArea
                rows={12}
                placeholder={`[
  {
    "id": "event_1",
    "time_offset": 0,
    "type": "video",
    "resource": "opening.mp4"
  },
  {
    "id": "event_2",
    "time_offset": 900,
    "type": "act_transition",
    "next_act": "act_2"
  }
]`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="开始条件" key="start_conditions">
            <Alert
              message="开始条件配置"
              description="配置时间线开始执行的条件，如前置任务完成、玩家就位等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="start_conditions" label="开始条件 (JSON)">
              <TextArea
                rows={6}
                placeholder={`{
  "player_count_min": 2,
  "all_players_ready": true,
  "prerequisite_quest": "quest_intro"
}`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="结束条件" key="end_conditions">
            <Alert
              message="结束条件配置"
              description="配置时间线结束的条件，如所有任务完成、游戏胜利/失败等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="end_conditions" label="结束条件 (JSON)">
              <TextArea
                rows={6}
                placeholder={`{
  "all_objectives_completed": true,
  "or": [
    {"condition": "sw_evidence_delivered"},
    {"condition": "sw_survived"}
  ]
}`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="时间线参考" key="reference">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="常用时间偏移">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li><Text code>0</Text> - 游戏开始</li>
                    <li><Text code>180</Text> - 3分钟后</li>
                    <li><Text code>600</Text> - 10分钟后</li>
                    <li><Text code>900</Text> - 15分钟（第一幕结束）</li>
                    <li><Text code>2700</Text> - 45分钟（第三幕开始）</li>
                    <li><Text code>6300</Text> - 105分钟（游戏结束）</li>
                  </ul>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="事件类型">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li><Text code>video</Text> - 播放视频</li>
                    <li><Text code>broadcast</Text> - 系统广播</li>
                    <li><Text code>act_transition</Text> - 幕转换</li>
                    <li><Text code>pause</Text> - 暂停等待</li>
                    <li><Text code>ending_roll</Text> - 结局结算</li>
                    <li><Text code>game_end</Text> - 游戏结束</li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs

export default TimelineEditorForm
